import { Controller } from "stimulus";
import Dropzone from "dropzone";
import {
  overrideDropzoneCancel,
  createDirectUploadController,
  removeElement,
  removeBlob,
  findElement,
} from "../../dropzone/media_helper";

Dropzone.autoDiscover = false;

const createDropZone = function (controller) {
  let dropzone = controller.dropzoneElement;

  return new Dropzone(dropzone, {
    url: controller.url,
    maxFiles: controller.maxFiles,
    maxFilesize: controller.maxFileSize,
    acceptedFiles: controller.acceptedFiles,
    addRemoveLinks: controller.addRemoveLinks,
    autoQueue: false,
    queuecomplete: function () {
      controller.dispatch("queue-complete", {
        detail: {
          disabled: false,
        },
      });
    },
    multiple: controller.multiple,
    clickable: controller.clickableElements,
  });
};

export default class extends Controller {
  static targets = [
    "input",
    "dropzoneDiv",
    "successFileUpload",
    "successFileName",
    "warningField",
    "alert",
    "alertText",
    "submitButton",
  ];

  connect() {
    overrideDropzoneCancel();

    this.uploadTimeouts = new Map(); // Track pending upload timeouts
    this.dropZone = createDropZone(this);
    this.hideFileInput();
    this.bindEvents();

    if (this.hasWarningFieldTarget) {
      this._hideElement(this.dropzoneDivTarget);
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true;
    }
  }

  disconnect() {
    this.uploadTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.uploadTimeouts.clear();

    if (this.dropZone) {
      this.dropZone.destroy();
    }
  }

  get url() {
    return this.inputTarget.getAttribute("data-direct-upload-url");
  }

  get maxFiles() {
    const maxFiles = this.inputTarget.dataset.maxFiles;
    return maxFiles ? parseInt(maxFiles, 10) : 1;
  }

  get maxFileSize() {
    const maxFilesize = this.inputTarget.dataset.maxFilesize;
    if (maxFilesize) {
      return parseFloat(maxFilesize);
    }
    return parseFloat((100 / 1.049).toFixed(4));
  }

  get acceptedFiles() {
    const acceptedFiles = this.inputTarget.dataset.acceptedFiles;
    return acceptedFiles || null;
  }

  get addRemoveLinks() {
    const addRemoveLinks = this.inputTarget.dataset.addRemoveLinks;
    if (addRemoveLinks !== undefined) {
      return addRemoveLinks === "true";
    }
    return this.data.get("addRemoveLinks") || true;
  }

  get multiple() {
    const multiple = this.inputTarget.dataset.multiple;
    return multiple !== undefined ? multiple === "true" : true;
  }

  get dropzoneElement() {
    return findElement(this.element, "#dropzone");
  }

  get isClickableRestricted() {
    return this.inputTarget.dataset.restrictClickable === "true";
  }

  get clickableTriggers() {
    const triggers =
      this.dropzoneElement?.querySelectorAll(
        '[data-dropzone-trigger="true"]',
      ) || [];

    if (!triggers.length) {
      console.error(
        `No dropzone triggers found for restricted clickable upload (${this.inputTarget.id}). File dialog will be disabled.`,
      );
      return [];
    }

    return Array.from(triggers);
  }

  get clickableElements() {
    if (!this.isClickableRestricted) {
      return this.dropzoneElement;
    }

    const triggers = this.clickableTriggers;
    return triggers.length ? triggers : false;
  }

  hideFileInput() {
    this.inputTarget.disabled = true;
    this.inputTarget.style.display = "none";
  }

  updateSubmitButtonState() {
    if (!this.hasSubmitButtonTarget) return;

    const hasSuccessfulFiles = this.dropZone.files.some(
      (f) => f.status === "success",
    );
    this.submitButtonTarget.disabled = !hasSuccessfulFiles;
  }

  _showElement(element, displayClass = "d-flex") {
    element.classList.add(displayClass);
    element.classList.remove("d-none");
  }

  _hideElement(element) {
    element.classList.add("d-none");
    element.classList.remove("d-flex", "d-block", "d-inline");
  }

  uploadAgain() {
    this._showElement(this.dropzoneDivTarget);
    this._hideElement(this.warningFieldTarget);
    this.dispatch("upload-again", {
      detail: {
        disabled: true,
      },
    });
  }

  changeFile() {
    this._showElement(this.dropzoneDivTarget);
    this._hideElement(this.successFileUploadTarget);
    this.dropZone.removeAllFiles();
    this.dispatch("change-file", {
      detail: {
        disabled: true,
      },
    });
  }

  bindEvents() {
    this.dropZone.on("addedfile", (file) => {
      const timeoutId = setTimeout(() => {
        // Only upload if file is still in dropzone (not removed)
        if (file.accepted && this.dropZone.files.includes(file)) {
          createDirectUploadController(
            this,
            file,
            this.inputTarget.name,
          ).start();
        }
        this.uploadTimeouts.delete(file);
      }, 500);

      this.uploadTimeouts.set(file, timeoutId);
    });

    if (this.maxFiles === 1) {
      this.dropZone.on("maxfilesexceeded", (file) => {
        if (this.hasAlertTarget) {
          this._showElement(this.alertTarget, "d-block");
          this.alertTextTarget.textContent =
            "Only one file can be uploaded. The latest file will replace any previous selection.";
        }

        this.dropZone.removeAllFiles();
        this.dropZone.addFile(file);

        if (this.hasAlertTarget) {
          setTimeout(() => {
            this._hideElement(this.alertTarget);
            this.alertTextTarget.textContent = "";
          }, 3000);
        }
      });
    }

    if (this.inputTarget.id != "course_builder_files") {
      this.dropZone.on("success", (file) => {
        if (
          !["ai_to_course_file", "pdf_to_course_file"].includes(
            this.inputTarget.id,
          )
        ) {
          this._hideElement(this.dropzoneDivTarget);
          this._showElement(this.successFileUploadTarget);
          this.successFileNameTarget.innerHTML = file.name;
        }

        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.disabled = false;
        }
      });
    }

    this.dropZone.on("error", (file, errorMessage) => {
      if (this.hasAlertTarget) {
        this._showElement(this.alertTarget, "d-block");
        const existingText = this.alertTextTarget.textContent;
        this.alertTextTarget.textContent = existingText
          ? `${existingText}, ${errorMessage}`
          : errorMessage;

        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.disabled = true;
        }
      }
    });

    this.dropZone.on("removedfile", (file) => {
      if (this.uploadTimeouts.has(file)) {
        clearTimeout(this.uploadTimeouts.get(file));
        this.uploadTimeouts.delete(file);
      }

      if (file.controller && file.status === "success") {
        let element = file.controller.hiddenInput;
        removeElement(element);
      }

      if (this.hasAlertTarget) {
        this.alertTextTarget.textContent = "";
        const errorMessages = this.dropZone.files
          .filter((f) => f.status === "error")
          .map(
            (f) =>
              f.previewElement.querySelector(".dz-error-message")?.textContent,
          )
          .filter(Boolean);

        if (errorMessages.length) {
          this.alertTextTarget.textContent = errorMessages.join(", ");
          this._showElement(this.alertTarget, "d-block");
          if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = true;
          }
        } else {
          this._hideElement(this.alertTarget);
          this.updateSubmitButtonState();
        }
      } else {
        this.updateSubmitButtonState();
      }
    });

    this.dropZone.on("canceled", (file) => {
      file.controller && file.controller.xhr.abort();
    });
  }
}